/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AzureCommunicationTokenCredential } from "@azure/communication-common";
import {
  CallClient,
  TeamsCallAgent,
  TeamsCall,
} from "@azure/communication-calling";

import qs from "query-string";

import { useLocation, useNavigate } from "react-router-dom";

import TeamsCallModal from "../TeamsAudioCall/TeamsCallModal";
import { useCallSounds } from "../../../lib/hooks/useCallSounds";
import { useRingTimeout } from "../../../lib/hooks/useRingTimeout";

const TeamsAudioGroupCall = () => {
  const navigate = useNavigate();
  const { search } = useLocation();

  const query = qs.parse(search);

  const callerAcsToken = query.callerAcsToken as string;
  const targetTeamsUserIds = query.targetTeamsUserIds as string;

  const allTargetUserIds = useMemo(
    () => targetTeamsUserIds?.split(",").map((id) => id.trim()) || [],
    [targetTeamsUserIds],
  );

  const [callingState, setCallingState] =
    useState<TeamsCallStatus>("connecting");

  const [triggerCall, setTriggerCall] = useState<boolean>(false);
  const [callsStarted, setCallsStarted] = useState<boolean>(false);

  const [teamsCallAgent, setTeamsCallAgent] = useState<TeamsCallAgent | null>(
    null,
  );
  const [activeCalls, setActiveCalls] = useState<TeamsCall[]>([]);
  const [connectedCall, setConnectedCall] = useState<TeamsCall | null>(null);

  const activeCallsRef = useRef<TeamsCall[]>([]);
  const connectedCallRef = useRef<TeamsCall | null>(null);

  const {
    startSound,
    ringingSound,
    connectedSound,
    endSound,
    playSound,
    stopAllSounds,
  } = useCallSounds();

  useEffect(() => {
    activeCallsRef.current = activeCalls;
  }, [activeCalls]);

  useEffect(() => {
    connectedCallRef.current = connectedCall;
  }, [connectedCall]);

  useEffect(() => {
    if (
      callingState === "disconnected" ||
      callingState === "error" ||
      callingState === "noAnswer"
    ) {
      const timeout = setTimeout(() => {
        navigate("/", { replace: true });
      }, 5000);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [callingState, navigate]);

  const startCall = useCallback(async () => {
    if (!teamsCallAgent || allTargetUserIds.length === 0) {
      console.log(
        "Cannot start call: Target Teams User Ids are missing or Teams Call Agent not initialized.",
      );
      setCallingState("error");
      return;
    }

    if (callsStarted) {
      return;
    }

    setCallsStarted(true);

    try {
      setCallingState("connecting");
      playSound(startSound);

      const calls: TeamsCall[] = [];

      await Promise.all(
        allTargetUserIds.map(async (userId) => {
          console.log("Starting call to user:", userId);

          try {
            const call = teamsCallAgent.startCall({
              microsoftTeamsUserId: userId.trim(),
            });

            calls.push(call);

            call.on("stateChanged", async () => {
              console.log(`Call ${call.id} to ${userId} state: ${call.state}`);

              if (call.state === "Connecting" && !connectedCallRef.current) {
                playSound(startSound);
                setCallingState("connecting");
              }

              if (call.state === "Ringing" && !connectedCallRef.current) {
                playSound(ringingSound);
                setCallingState("ringing");
              }

              if (call.state === "Connected" && !connectedCallRef.current) {
                const participant = call.remoteParticipants[0];
                let accepted = false;

                try {
                  await call.muteIncomingAudio();
                  console.log("Incoming audio muted for voicemail detection");
                } catch (error) {
                  console.log("Failed to mute incoming audio:", error);
                }

                const handleStreamsUpdated = async () => {
                  const videoStreams = participant.videoStreams || [];

                  if (videoStreams.length > 0 && !accepted) {
                    accepted = true;
                    participant.off(
                      "videoStreamsUpdated",
                      handleStreamsUpdated,
                    );
                    console.log("Valid stream found, accepting call");

                    try {
                      await call.unmuteIncomingAudio();
                      console.log("Incoming audio unmuted.");
                    } catch (error) {
                      console.log("Failed to unmute incoming audio:", error);
                    }

                    connectedCallRef.current = call;
                    setConnectedCall(call);
                    playSound(connectedSound);
                    setCallingState("connected");

                    const otherCalls = activeCallsRef.current.filter(
                      (c) => c.id !== call.id && c.state !== "Disconnected",
                    );

                    await Promise.all(
                      otherCalls.map((c) =>
                        c
                          .hangUp()
                          .catch((e) => console.log("HangUp failed", e)),
                      ),
                    );
                  }
                };

                participant.on("videoStreamsUpdated", handleStreamsUpdated);

                setTimeout(async () => {
                  if (!accepted) {
                    console.log(
                      "Timeout passed with no valid stream → treating as voicemail",
                    );

                    try {
                      await call.hangUp();
                    } catch (err) {
                      console.log("Failed to hang up voicemail call:", err);
                    }

                    participant.off(
                      "videoStreamsUpdated",
                      handleStreamsUpdated,
                    );
                  }
                }, 4000);
              }

              if (call.state === "Disconnected") {
                console.log(`Call ${call.id} disconnected`);

                const noAcceptedCalls = !connectedCallRef.current;
                const allDisconnected = activeCallsRef.current.every(
                  (c) => c.state === "Disconnected",
                );

                if (noAcceptedCalls && allDisconnected) {
                  console.log("All calls disconnected and none were accepted.");
                  playSound(endSound);
                  setCallingState("noAnswer");
                }

                if (connectedCallRef.current?.id === call.id) {
                  playSound(endSound);
                  setCallingState("disconnected");
                }
              }
            });
          } catch (error) {
            console.log(`Failed to start call to ${userId}:`, error);
            setCallingState("error");
          }
        }),
      );

      setActiveCalls(calls);
      console.log(`Started ${calls.length} parallel calls`);
    } catch (error) {
      console.log("Failed to start calls: ", error);
      setCallingState("error");
    }
  }, [teamsCallAgent, allTargetUserIds, callsStarted]);

  useEffect(() => {
    if (triggerCall && !callsStarted) {
      startCall();
    }
  }, [triggerCall, callsStarted, startCall]);

  const handleRingTimeout = useCallback(async () => {
    console.log("No call was answered in time. Hanging up the ringing ones.");

    // The state handler above turns the last disconnect into `noAnswer`, so
    // hanging up is all this has to do.
    await Promise.all(
      activeCallsRef.current
        .filter((call) => call.state !== "Disconnected")
        .map((call) =>
          call
            .hangUp()
            .catch((error) =>
              console.log("Failed to hang up unanswered call:", error),
            ),
        ),
    );
  }, []);

  useRingTimeout(callingState === "ringing", handleRingTimeout);

  const hangUpCall = useCallback(async () => {
    console.log("Hanging up all calls...");

    await Promise.all(
      activeCallsRef.current
        .filter((call) => call.state !== "Disconnected")
        .map(async (call) => {
          try {
            console.log(`Hanging up call ${call.id}`);
            await call.hangUp();
          } catch (error) {
            console.log("Failed to hang up call:", call.id, error);
          }
        }),
    );

    setActiveCalls([]);
    setConnectedCall(null);
    connectedCallRef.current = null;

    playSound(endSound);
    setCallingState("disconnected");
  }, []);

  const handleClose = useCallback(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const initializeCallAgent = async () => {
      if (!callerAcsToken) {
        navigate("/", { replace: true });
        return;
      }

      try {
        const client = new CallClient();
        const tokenCredential = new AzureCommunicationTokenCredential(
          callerAcsToken.trim(),
        );
        const agent = await client.createTeamsCallAgent(tokenCredential);

        setTeamsCallAgent(agent);

        const deviceManager = await client.getDeviceManager();
        await deviceManager.askDevicePermission({ video: true, audio: true });

        console.log("Call agent initialized.");

        setTriggerCall(true);
      } catch (error) {
        console.log("Failed to initialize call agent:", error);
        setCallingState("error");
      }
    };

    initializeCallAgent();
  }, [callerAcsToken, navigate, startCall]);

  useEffect(() => {
    return () => {
      stopAllSounds();
      (async () => {
        await Promise.all(
          activeCallsRef.current
            .filter((call) => call.state !== "Disconnected")
            .map(async (call) => {
              try {
                await call.hangUp();
              } catch (error) {
                console.log("Failed to hang up call:", call.id, error);
              }
            }),
        );

        setActiveCalls([]);
        setConnectedCall(null);
      })();
    };
  }, []);

  return (
    <TeamsCallModal
      status={callingState}
      onEndCall={hangUpCall}
      onClose={handleClose}
    />
  );
};

export default TeamsAudioGroupCall;
